import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from './Button';
import { submitReservation } from './reservationApi';
import './ReservationForm.css';

const EMPTY = {
  startDate: '',
  endDate: '',
  name: '',
  email: '',
  phone: '',
  termsAccepted: false,
};

function ReservationForm({ vehicleSlug, unavailableDates = [], selection, termsVersion }) {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedStart = selection ? selection.startDate : '';
  const selectedEnd = selection ? selection.endDate : '';

  // Picking on the calendar and typing in the fields are two routes to the same
  // two values, so the calendar's choice is mirrored here rather than kept as a
  // second source of truth the two could disagree about.
  useEffect(() => {
    setValues((previous) => ({
      ...previous,
      startDate: selectedStart || previous.startDate,
      endDate: selectedEnd || previous.endDate,
    }));
  }, [selectedStart, selectedEnd]);

  function update(field, value) {
    setValues((previous) => ({ ...previous, [field]: value }));
  }

  function updateStartDate(startDate) {
    setValues((previous) => ({
      ...previous,
      startDate,
      endDate: previous.endDate && previous.endDate < startDate ? '' : previous.endDate,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);

    const result = await submitReservation(vehicleSlug, {
      ...values,
      termsVersion: termsVersion || '',
    });

    setSubmitting(false);

    if (result.outcome === 'pending') {
      setErrors([]);
      setConfirmation(result.reservationId);
      setValues(EMPTY);
      return;
    }

    setErrors(result.errors);
  }

  if (confirmation) {
    return (
      <div className="reservation-form__confirmation" role="status">
        <h3>Request received</h3>
        <p>
          Your dates are held while we review the request. We will email you to confirm or
          decline. Nothing is booked until you hear back from us.
        </p>
      </div>
    );
  }

  return (
    <form className="reservation-form" onSubmit={handleSubmit} noValidate>
      <h2>Request to Reserve</h2>

      {errors.length > 0 && (
        <div className="reservation-form__errors" role="alert">
          <p>Please fix the following:</p>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="reservation-form__dates">
        <label htmlFor="startDate">
          Start date
          <input
            id="startDate"
            type="date"
            value={values.startDate}
            onChange={(event) => updateStartDate(event.target.value)}
          />
        </label>

        <label htmlFor="endDate">
          End date
          <input
            id="endDate"
            type="date"
            min={values.startDate || undefined}
            value={values.endDate}
            onChange={(event) => update('endDate', event.target.value)}
          />
        </label>
      </div>

      <label htmlFor="name">
        Your name
        <input
          id="name"
          type="text"
          autoComplete="name"
          value={values.name}
          onChange={(event) => update('name', event.target.value)}
        />
      </label>

      <label htmlFor="email">
        Email
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={(event) => update('email', event.target.value)}
        />
      </label>

      <label htmlFor="phone">
        Phone (optional)
        <input
          id="phone"
          type="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={(event) => update('phone', event.target.value)}
        />
      </label>

      <div className="reservation-form__terms">
        <input
          id="termsAccepted"
          type="checkbox"
          aria-label="I agree to the Terms and Conditions"
          checked={values.termsAccepted}
          onChange={(event) => update('termsAccepted', event.target.checked)}
        />
        <span>
          I agree to the{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">
            Terms and Conditions
          </Link>
        </span>
      </div>

      <Button variant="primary" type="submit" disabled={submitting}>
        {submitting ? 'Sending...' : 'Request to Reserve'}
      </Button>
    </form>
  );
}

export default ReservationForm;
